import React from "react";
import SectionHeader from "../../common/section-header";
import TextInput from "../../common/text-input";
import DropdownComponent from "../../common/dropdown-input";
import Text from "../../common/typography/text.js";
import AssociatedActivity from "./AssociatedActivity";
import {
  ACCOUNT_USER_TYPE_OPTIONS,
  USER_TYPES,
} from "../../Service/userService";
import "./UserDetails.css";

/**
 * Details tab of the User page.
 *
 * Controlled by the parent (UserDetailsPage): `values` + `setValue` hold the
 * editable form state, `isEditable` toggles the disabled state on every field.
 *
 * The Role-Specific section renders a single field chosen by `user.UserType`:
 *   Admin        -> Region (formerly the Viewer role, now merged into Admin)
 *   Account      -> Account
 *   Manufacturer -> Manufacturer
 *   Distributor  -> Distributor (+ Manager toggle)
 */
const UserDetailsTab = ({
  user,
  values,
  setValue,
  isEditable,
  canEditAccountUserType = false,
  canEditEmail = false,
  hasPortalAccess = false,
  nextActivityCursor,
  onLoadMoreActivity,
  activityError,
  onRefreshActivity,
  options = {},
  activity,
  activityLoading,
}) => {
  const userType = user?.UserType;
  const isAccountUser = userType === USER_TYPES.ACCOUNT;
  const isProviderSelection =
    isAccountUser &&
    (user?.IsProvider || values.AccountContactType === "Provider");
  // With portal access a provider's contact email is their login email.
  const providerHasAccess = isProviderSelection && hasPortalAccess;

  // Each role-specific config maps a UserType to its field. `options[key]` are
  // the selectable options once the backend option endpoints exist; until then
  // the current value is shown and the control stays effectively read-only.
  //
  // `readOnly` marks the associations a user cannot be reassigned to from this
  // page — moving someone to a different Account / Manufacturer / Distributor
  // is not something `updateuser` supports.
  const roleField = {
    [USER_TYPES.ADMIN]: {
      key: "Region",
      label: "Region",
      placeholder: "Select a Region",
      valueField: "RegionName",
      options: options.regions,
    },
    [USER_TYPES.ACCOUNT]: {
      key: "Account",
      label: "Account",
      placeholder: "Select an Account",
      valueField: "AccountName",
      options: options.accounts,
      readOnly: true,
    },
    [USER_TYPES.MANUFACTURER]: {
      key: "Manufacturer",
      label: "Manufacturer",
      placeholder: "Select a Manufacturer",
      valueField: "ManufacturerName",
      options: options.manufacturers,
      readOnly: true,
    },
    [USER_TYPES.DISTRIBUTOR]: {
      key: "Distributor",
      label: "Distributor",
      placeholder: "Select a Distributor",
      valueField: "DistributorName",
      options: options.distributors,
      readOnly: true,
    },
  }[userType];

  const renderRoleField = () => {
    const currentName = values[roleField.valueField];
    return (
      <div className="user-details-role-row">
        {isAccountUser && (
          <DropdownComponent
            parentClassname="user-details-field"
            label="Account User Type"
            placeholder="Select an Account User Type"
            isDisabled={
              !isEditable || !canEditAccountUserType || user?.IsProvider
            }
            value={
              values.AccountContactType
                ? {
                    label: values.AccountContactType,
                    value: values.AccountContactType,
                  }
                : null
            }
            options={ACCOUNT_USER_TYPE_OPTIONS}
            onChange={(opt) => setValue("AccountContactType", opt?.value ?? "")}
          />
        )}
        <DropdownComponent
          parentClassname="user-details-field"
          label={roleField.label}
          placeholder={roleField.placeholder}
          isDisabled={roleField.readOnly || !isEditable}
          value={
            currentName ? { label: currentName, value: currentName } : null
          }
          options={roleField.options || []}
          onChange={(opt) => setValue(roleField.valueField, opt?.label ?? "")}
        />
        {userType === USER_TYPES.DISTRIBUTOR && (
          <div className="user-details-manager-card">
            <Text variant="tinyText">Manager</Text>
            <label className="user-details-manager-toggle">
              <input
                type="checkbox"
                checked={!!values.IsManager}
                disabled={!isEditable}
                onChange={(e) => setValue("IsManager", e.target.checked)}
              />
            </label>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="user-details-tab">
      <div className="user-details-main">
        {/* General */}
        <SectionHeader title="General" showButton={false} />
        <div className="user-details-field-row">
          {isProviderSelection && (
            <TextInput
              parentClassName="user-details-field"
              titleText="Salutation"
              placeholder="Dr."
              isDisabled={!isEditable || !canEditAccountUserType}
              value={values.Prefix}
              setValue={(v) => setValue("Prefix", v)}
            />
          )}
          <TextInput
            parentClassName="user-details-field"
            titleText="First Name"
            placeholder="Enter First Name"
            isRequired={isProviderSelection}
            isDisabled={!isEditable}
            value={values.FirstName}
            setValue={(v) => setValue("FirstName", v)}
          />
          <TextInput
            parentClassName="user-details-field"
            titleText="Last Name"
            placeholder="Enter Last Name"
            isRequired={isProviderSelection}
            isDisabled={!isEditable}
            value={values.LastName}
            setValue={(v) => setValue("LastName", v)}
          />
          {isProviderSelection ? (
            <TextInput
              parentClassName="user-details-field"
              titleText="Provider Contact Email"
              helperText={
                providerHasAccess
                  ? "Also the login email. Changing it suspends access and sends an invitation requiring a new password."
                  : "Also used as the login email if this provider is invited."
              }
              placeholder="provider@example.com"
              isDisabled={
                !isEditable ||
                !canEditAccountUserType ||
                (providerHasAccess && !canEditEmail)
              }
              value={values.ContactEmail}
              setValue={(v) => setValue("ContactEmail", v)}
            />
          ) : (
            <TextInput
              parentClassName="user-details-field"
              titleText="Email"
              placeholder="No email on file"
              isDisabled={!isEditable || !canEditEmail}
              helperText={
                user?.EmailChangeEnabled === false
                  ? "Email changes are not currently enabled."
                  : canEditEmail
                    ? "Changing email suspends access and sends an invitation requiring a new password."
                    : "Editing login email requires Edit and Invite permissions."
              }
              value={values.Email ?? user?.Email ?? ""}
              setValue={(v) => setValue("Email", v)}
            />
          )}
          <TextInput
            parentClassName="user-details-field"
            titleText="Phone"
            placeholder="Phone"
            isDisabled={
              !isEditable || (isProviderSelection && !canEditAccountUserType)
            }
            value={values.Phone}
            setValue={(v) => setValue("Phone", v)}
          />
          {isProviderSelection && (
            <TextInput
              parentClassName="user-details-field"
              titleText="Provider NPI"
              placeholder="10-digit NPI"
              isRequired
              isDisabled={!isEditable || !canEditAccountUserType}
              value={values.ProviderNPI}
              setValue={(v) =>
                setValue(
                  "ProviderNPI",
                  String(v || "")
                    .replace(/\D/g, "")
                    .slice(0, 10),
                )
              }
            />
          )}
        </div>

        {/* Role-Specific — only shown for roles that carry a scoping field. */}
        {roleField && (
          <>
            <SectionHeader title="Role-Specific" showButton={false} />
            {renderRoleField()}
          </>
        )}
      </div>

      <AssociatedActivity
        activity={activity}
        loading={activityLoading}
        nextCursor={nextActivityCursor}
        onLoadMore={onLoadMoreActivity}
        error={activityError}
        onRefresh={onRefreshActivity}
      />
    </div>
  );
};

export default UserDetailsTab;
